/**
 * Empty State - the honest "nothing here (and why)" block in the F language.
 * Rounded-square icon tile (the F tile, not a circle), iconify-safe icon
 * handling, an optional dashed variant matching the public pages' inline
 * empty boxes, and role="status" so assistive tech announces the state.
 */

import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Icon } from '@iconify/react';
import { useThemeMode, radius } from '../../context/ThemeContext';

function EmptyState({
  icon,
  title = 'No data available',
  description = 'There is no data to display at this time.',
  actionLabel,
  onAction,
  compact = false,     // tighter spacing for use inside a chart/card area
  variant = 'plain',   // 'plain' | 'dashed' (the public dashed empty box)
  sx,
}) {
  const { palette } = useThemeMode();
  const tile = compact ? 52 : 72;
  const glyph = compact ? 24 : 32;

  // Iconify elements size via width/height attributes; MUI icons via sx.
  // Render provided elements as-is (callers size them), otherwise show the
  // default iconify tray glyph.
  const glyphNode = React.isValidElement(icon) ? (
    icon
  ) : (
    <Icon icon="ph:tray" width={glyph} height={glyph} color={palette.textSecondary} />
  );

  const dashed = variant === 'dashed';

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: compact ? 4 : 8,
        px: 3,
        textAlign: 'center',
        height: compact ? '100%' : 'auto',
        ...(dashed && {
          border: `1px dashed ${palette.cardBorder}`,
          borderRadius: `${radius.control}px`,
          bgcolor: palette.surface,
        }),
        ...sx,
      }}
    >
      <Box
        sx={{
          width: tile,
          height: tile,
          borderRadius: `${radius.sm}px`,
          backgroundColor: palette.surfaceHover,
          border: `1px solid ${palette.cardBorder}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: compact ? 2 : 3,
          color: palette.textSecondary,
        }}
      >
        {glyphNode}
      </Box>
      <Typography
        component="p"
        sx={{ fontWeight: 700, color: palette.text, mb: compact ? 0.5 : 1, fontSize: compact ? '0.95rem' : '1.1rem' }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: palette.textMuted, maxWidth: 360, lineHeight: 1.6, fontSize: compact ? '0.82rem' : undefined }}
      >
        {description}
      </Typography>
      {actionLabel && onAction && (
        <Button
          variant="outlined"
          onClick={onAction}
          sx={{
            mt: 3,
            borderColor: palette.cardBorder,
            color: palette.text,
            fontWeight: 600,
            '&:hover': {
              borderColor: palette.accent,
              backgroundColor: palette.accentBg,
            },
          }}
        >
          {actionLabel}
        </Button>
      )}
    </Box>
  );
}

export default EmptyState;
